import React, { useState, useEffect } from 'react';
import { headerStyle, inputStyle, noteStyle } from '../../constants/styles';
import clsx from 'clsx';
import BasicCard from '../BasicCard';
import CustomButton from '../CustomButton';
import Header from '../Header';
import InputCardItems from '../InputCardItems';
import Select, { MultiValue } from 'react-select';
import apiRequest from '../../utils/apiRequest';
import generateDiscountCode from '../../utils/generateDiscountCode';
import getCollectionList from '../../utils/getCollectionList';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Loader from '../../global/loader';

type DiscountByTypeParams = {
  discountType: 'percentage' | 'amount';
  rewardTypeId: string;
  rewardId: string;
};

interface TireData {
  tierName: string;
  tierIcon: string;
  tierId: string;
}

type CollectionType = {
  value: string;
  label: string;
};

type CouponDetailsByIdResponse = {
  data: {
    codeDiscountNode: {
      id: string;
      codeDiscount: {
        excludedTiers: {
          value: string;
          label: string;
        }[];
        includedTiers: {
          value: string;
          label: string;
        }[];
        title: string;
        summary: string;
        status: string;
        endsAt: string;
        customerGets: {
          items: {
            collections: {
              nodes: {
                id: string;
                title: string;
              }[];
            };
          };
          value: {
            percentage?: string;
            amount?: {
              amount: string;
            };
          };
        };
        minimumRequirement: {
          greaterThanOrEqualToSubtotal: {
            amount: string;
          };
        };
      };
    };
  };
};
type FormData = {
  typeId: string;
  isLimited: boolean;
  includedTierId: string;
  excludedTierId: string;
  isActive: string;
  discountCode: string;
  discountCodePrefix: string;
  isDiscountCodePrefixRequired: boolean;
  minimumRequirement: string;
  minimumOrderValueRequired: string;
  expiryDate: string;
  expiryTime: string;
  isRewardExpiry: boolean;
  isEntireOrder: string;
  collection: string;
  discountAmount: string;
  discountPercentage: string;
  tierSelection: string;
  includedTierIds: CollectionType[];
  excludedTierIds: CollectionType[];
};

const DiscountByType = ({
  discountType,
  rewardTypeId,
  rewardId,
}: DiscountByTypeParams) => {
  const [tireData, setTierData] = useState<CollectionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [collectionList, setCollectionList] = useState<CollectionType[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<
    CollectionType[]
  >([]);
  const [initialCollections, setInitialCollections] = useState<
    CollectionType[]
  >([]);

  const [currentDate, setCurrentDate] = useState('');
  const [currentTime, setCurrentTime] = useState('');

  const [formData, setFormData] = useState<FormData>({
    typeId: rewardTypeId,
    isLimited: false,
    includedTierId: '',
    excludedTierId: '',
    isActive: 'active',
    discountCode: '',
    discountCodePrefix: '',
    isDiscountCodePrefixRequired: false,
    minimumRequirement: 'false',
    minimumOrderValueRequired: '0',
    expiryDate: '',
    expiryTime: '',
    isRewardExpiry: false,
    isEntireOrder: '',
    collection: '',
    discountAmount: '',
    discountPercentage: '',
    tierSelection: 'excluded',
    includedTierIds: [],
    excludedTierIds: [],
  });

  const navigation = useNavigate();

  const markCurrentDateAndTime = () => {
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const time = now.toTimeString().slice(0, 5);

    setCurrentDate(date);
    setCurrentTime(time);
  };

  const handleCollectionSelect = (
    selectedOptions: MultiValue<CollectionType>
  ) => {
    setSelectedCollections(selectedOptions as CollectionType[]);
  };

  const getCollectionListData = async () => {
    const collectionListData = await getCollectionList();

    if (collectionListData) {
      setCollectionList(collectionListData || []);
    }
  };

  const handleMultiSelectChange = (
    selectedOptions: any[] | MultiValue<{ value: string; label: string }>,
    fieldName: string
  ) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData({
      ...formData,
      [fieldName]: selectedOptions,
    });
  };

  const handleCreateAmountReward = async () => {
    const requestBody = {
      typeId: formData.typeId,
      discountCode: `${formData.discountCodePrefix}${formData.discountCode}`,
      discountAmount:
        discountType === 'amount'
          ? parseFloat(formData.discountAmount)
          : undefined,
      isEntireOrder: formData.isEntireOrder === 'true',
      collection:
        formData.isEntireOrder === 'false'
          ? selectedCollections.map((val) => val.value)
          : null,
      minimumRequirement:
        formData.minimumRequirement === 'true'
          ? parseFloat(formData.minimumOrderValueRequired)
          : null,
      expiryDateAndTime: formData.isRewardExpiry
        ? `${formData.expiryDate}T${formData.expiryTime}:00`
        : null,
      isLimited: formData.isLimited,
      isActive: formData.isActive === 'active',
      excludedTierIds:
        formData.excludedTierIds.length > 0 &&
        formData.tierSelection === 'excluded'
          ? formData.excludedTierIds.map((val) => val.value)
          : null,
      includedTierIds:
        formData.includedTierIds.length > 0 &&
        formData.tierSelection === 'included'
          ? formData.includedTierIds.map((val) => val.value)
          : null,
    };

    const res = await apiRequest('createReward', requestBody);

    if (res.error) {
      toast.error(res.error?.error || 'Failed to create, please try again');
    } else {
      toast.success('Reward created successfully');
      navigation(-1);
    }
  };

  const handleCreatePercentageReward = async () => {
    if (formData.discountPercentage) {
      if (parseFloat(formData.discountPercentage) > 100) {
        toast.error('Discount percentage cannot be greater than 100');
        return;
      }
    } else {
      toast.error('Discount percentage cannot be empty');
      return;
    }

    const requestBody = {
      typeId: formData.typeId,
      discountCode: `${formData.discountCodePrefix}${formData.discountCode}`,
      discountPercentage:
        discountType === 'percentage'
          ? parseFloat(formData.discountPercentage)
          : undefined,
      isEntireOrder: formData.isEntireOrder === 'true',
      collection:
        formData.isEntireOrder === 'false'
          ? selectedCollections.map((val) => val.value)
          : null,
      minimumRequirement:
        formData.minimumRequirement === 'true'
          ? parseFloat(formData.minimumOrderValueRequired)
          : null,
      expiryDateAndTime: formData.isRewardExpiry
        ? `${formData.expiryDate}T${formData.expiryTime}:00`
        : null,
      isLimited: formData.isLimited,
      isActive: formData.isActive === 'active',
      excludedTierIds:
        formData.excludedTierIds.length > 0 &&
        formData.tierSelection === 'excluded'
          ? formData.excludedTierIds.map((val) => val.value)
          : null,
      includedTierIds:
        formData.includedTierIds.length > 0 &&
        formData.tierSelection === 'included'
          ? formData.includedTierIds.map((val) => val.value)
          : null,
    };

    console.log('req body:', requestBody);

    const res = await apiRequest('createReward', requestBody);

    if (res.error) {
      toast.error(res.error?.error || 'Failed to create, please try again');
    } else {
      toast.success('Reward created successfully');
      navigation(-1);
    }
  };

  const handleUpdateAmountReward = async () => {
    const currentCollectionIds = selectedCollections.map((val) => val.value);
    const initialCollectionIds = initialCollections.map((val) => val.value);

    const collectionToAdd = currentCollectionIds.filter(
      (id) => !initialCollectionIds.includes(id)
    );
    const collectionToRemove = initialCollectionIds.filter(
      (id) => !currentCollectionIds.includes(id)
    );

    const requestBody = {
      rewardId,
      typeId: formData.typeId,
      discountCode: `${formData.discountCode}`,
      discountAmount:
        discountType === 'amount'
          ? parseFloat(formData.discountAmount)
          : undefined,
      isEntireOrder: formData.isEntireOrder === 'true',
      collectionToAdd: collectionToAdd.length > 0 ? collectionToAdd : null,
      collectionToRemove:
        collectionToRemove.length > 0 ? collectionToRemove : null,
      minimumRequirement:
        formData.minimumRequirement === 'true'
          ? parseFloat(formData.minimumOrderValueRequired)
          : null,
      expiryDateAndTime: formData.isRewardExpiry
        ? `${formData.expiryDate}T${formData.expiryTime}:00`
        : null,
      isLimited: formData.isLimited,
      isActive: formData.isActive === 'active',
      excludedTierIds:
        formData.excludedTierIds.length > 0 &&
        formData.tierSelection === 'excluded'
          ? formData.excludedTierIds.map((val) => val.value)
          : null,
      includedTierIds:
        formData.includedTierIds.length > 0 &&
        formData.tierSelection === 'included'
          ? formData.includedTierIds.map((val) => val.value)
          : null,
    };

    const res = await apiRequest('updateReward', requestBody);

    if (res.error) {
      toast.error(res.error?.error || 'Failed to update, please try again');
    } else {
      toast.success('Reward updated successfully');
      navigation(-1);
    }
  };

  const handleUpdatePercentageReward = async () => {
    if (formData.discountPercentage) {
      if (parseFloat(formData.discountPercentage) > 100) {
        toast.error('Discount percentage cannot be greater than 100');
        return;
      }
    } else {
      toast.error('Discount percentage cannot be empty');
      return;
    }

    const currentCollectionIds = selectedCollections.map((val) => val.value);
    const initialCollectionIds = initialCollections.map((val) => val.value);

    const collectionToAdd = currentCollectionIds.filter(
      (id) => !initialCollectionIds.includes(id)
    );
    const collectionToRemove = initialCollectionIds.filter(
      (id) => !currentCollectionIds.includes(id)
    );

    const requestBody = {
      rewardId,
      typeId: formData.typeId,
      discountCode: `${formData.discountCodePrefix}${formData.discountCode}`,
      discountPercentage:
        discountType === 'percentage'
          ? parseFloat(formData.discountPercentage)
          : undefined,
      isEntireOrder: formData.isEntireOrder === 'true',
      collectionToAdd: collectionToAdd.length > 0 ? collectionToAdd : null,
      collectionToRemove:
        collectionToRemove.length > 0 ? collectionToRemove : null,
      minimumRequirement:
        formData.minimumRequirement === 'true'
          ? parseFloat(formData.minimumOrderValueRequired)
          : null,
      expiryDateAndTime: formData.isRewardExpiry
        ? `${formData.expiryDate}T${formData.expiryTime}:00`
        : null,
      isLimited: formData.isLimited,
      isActive: formData.isActive === 'active',
      excludedTierIds:
        formData.excludedTierIds.length > 0 &&
        formData.tierSelection === 'excluded'
          ? formData.excludedTierIds.map((val) => val.value)
          : null,
      includedTierIds:
        formData.includedTierIds.length > 0 &&
        formData.tierSelection === 'included'
          ? formData.includedTierIds.map((val) => val.value)
          : null,
    };

    const res = await apiRequest('updateReward', requestBody);

    if (res.error) {
      toast.error(res.error?.error || 'Failed to update, please try again');
    } else {
      toast.success('Reward updated successfully');
      navigation(-1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (rewardId) {
      if (discountType === 'amount') {
        await handleUpdateAmountReward();
      } else {
        await handleUpdatePercentageReward();
      }
    } else {
      if (discountType === 'amount') {
        await handleCreateAmountReward();
      } else {
        await handleCreatePercentageReward();
      }
    }

    setIsLoading(false);
  };

  const handelGenerateRandomCode = () => {
    const discountCode = generateDiscountCode();
    console.log('discount code: ', discountCode);

    setFormData((prevData) => ({
      ...prevData,
      discountCode,
    }));
  };

  const getTierData = async () => {
    const res = await apiRequest('getAllTier');
    console.log(res);

    if (!res?.error && res?.status === 200) {
      const tierOptions: CollectionType[] = res.data.map((tier: TireData) => ({
        value: tier.tierId,
        label: tier.tierName,
      }));
      setTierData(tierOptions);
    }
  };

  const getRewardData = async () => {
    setIsLoading(true);

    const params = {
      couponId: rewardId,
    };

    const res = await apiRequest('getRewardById', null, params);
    setIsLoading(false);

    if (res.error) {
      toast.error('Failed to get reward data');
      navigation('/program');
    } else {
      const data: CouponDetailsByIdResponse = res.data;

      if (
        data.data &&
        data.data.codeDiscountNode &&
        data.data.codeDiscountNode.codeDiscount
      ) {
        const discount = data.data.codeDiscountNode.codeDiscount;
        console.log('discount', discount);

        const collections = discount.customerGets.items.collections
          ? discount.customerGets.items.collections.nodes.map((colData) => {
              return {
                value: colData.id,
                label: colData.title,
              };
            })
          : [];

        setSelectedCollections(collections);
        setInitialCollections(collections);

        setFormData((prevFormData) => ({
          ...prevFormData,
          discountCode: discount.title,
          discountAmount: discount.customerGets.value.amount?.amount || '',
          discountPercentage: discount.customerGets.value.percentage
            ? `${parseFloat(discount.customerGets.value.percentage) * 100}`
            : '',
          isEntireOrder: discount.customerGets.items.collections
            ? 'false'
            : 'true',
          minimumRequirement: discount.minimumRequirement ? 'true' : 'false',
          minimumOrderValueRequired:
            discount.minimumRequirement?.greaterThanOrEqualToSubtotal?.amount ||
            '0',
          isRewardExpiry: discount.endsAt ? true : false,
          expiryDate: discount.endsAt ? discount.endsAt.split('T')[0] : '',
          expiryTime: discount.endsAt
            ? discount.endsAt.split('T')[1].slice(0, 5)
            : '',
          isActive: discount.status === 'ACTIVE' ? 'active' : 'disabled',
          tierSelection:
            discount.excludedTiers.length > 0 ? 'excluded' : 'included',
          isLimited:
            discount.excludedTiers.length > 0 ||
            discount.includedTiers.length > 0
              ? true
              : false,
          includedTierIds: discount.includedTiers,
          excludedTierIds: discount.excludedTiers,
        }));
      }
    }
  };

  useEffect(() => {
    getCollectionListData();
    getTierData();
    markCurrentDateAndTime();
  }, []);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (rewardId) {
      getRewardData();
    }
  }, [rewardId]);

  return (
    <>
      {isLoading && <Loader showHide={isLoading} />}
      {!isLoading && (
        <div className='px-5'>
          <CustomButton
            name={isLoading ? 'Laoding...' : 'Save'}
            onClick={!isLoading ? handleSubmit : () => {}}
          />
          <Header
            title={
              discountType === 'amount'
                ? 'Amount Discount'
                : 'Percentage Discount'
            }
          />

          <div className='flex w-full gap-3'>
            <div className='w-[60%]'>
              <BasicCard>
                <div className='w-full'>
                  <InputCardItems>
                    <div className='w-full flex justify-between'>
                      <div className='w-[350px]'>
                        <div>Discount Code</div>
                        <input
                          type='text'
                          name='discountCode'
                          className={clsx(inputStyle)}
                          value={formData?.discountCode}
                          onChange={handleChange}
                        />
                        <div className={clsx(noteStyle)}>
                          Customers must enter this code to checkout.
                        </div>
                      </div>

                      <div
                        className='text-[#0148AF] text-[14px] underline font-medium cursor-pointer'
                        onClick={handelGenerateRandomCode}
                      >
                        Generate random code
                      </div>
                    </div>
                  </InputCardItems>
                </div>
              </BasicCard>

              <BasicCard>
                {discountType === 'amount' ? (
                  <InputCardItems>
                    <div className='w-full'>
                      <div>Discount Value</div>
                      <div className='flex gap-2'>
                        <input
                          type='text'
                          className={clsx(inputStyle, 'w-[30%]')}
                          placeholder='Amount'
                          disabled
                        />
                        <input
                          type='text'
                          className={clsx(inputStyle, 'w-[45%]')}
                          placeholder='Enter Amount'
                          name='discountAmount'
                          value={formData?.discountAmount}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </InputCardItems>
                ) : (
                  <InputCardItems>
                    <div className='w-full'>
                      <div>Discount Value</div>
                      <div className='flex gap-2'>
                        <input
                          type='text'
                          className={clsx(inputStyle, 'w-[30%]')}
                          placeholder='Percentage'
                          disabled
                        />
                        <input
                          type='number'
                          className={clsx(inputStyle, 'w-[45%]')}
                          placeholder='Enter Percentage %'
                          name='discountPercentage'
                          value={formData.discountPercentage}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </InputCardItems>
                )}
              </BasicCard>

              <BasicCard>
                <div>
                  <div>
                    <div className={headerStyle}>Applies To</div>

                    <div className='mt-3'>
                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='isEntireOrder'
                          value='true'
                          checked={formData.isEntireOrder === 'true'}
                          onChange={handleChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Entire Order
                        </label>
                      </div>

                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='isEntireOrder'
                          value='false'
                          onChange={handleChange}
                          checked={formData.isEntireOrder === 'false'}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Specific Collection
                        </label>
                      </div>

                      <Select
                        isMulti
                        name='colors'
                        isDisabled={formData.isEntireOrder === 'true'}
                        defaultValue={selectedCollections}
                        options={collectionList}
                        onChange={handleCollectionSelect}
                        className={clsx('w-[50%] my-2')}
                      />
                    </div>
                  </div>
                </div>
              </BasicCard>

              <BasicCard>
                <div>
                  <div>
                    <div className={headerStyle}>Minimum Requirement</div>

                    <div className='mt-3'>
                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='minimumRequirement'
                          value='false'
                          checked={formData.minimumRequirement === 'false'}
                          onChange={handleChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>None</label>
                      </div>

                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='minimumRequirement'
                          value='true'
                          checked={formData.minimumRequirement === 'true'}
                          onChange={handleChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Minimum Purchase Amount
                        </label>
                      </div>

                      <div>
                        <input
                          className={clsx(inputStyle, 'w-[350px]')}
                          type='number'
                          disabled={formData.minimumRequirement !== 'true'}
                          name='minimumOrderValueRequired'
                          onChange={handleChange}
                          value={formData.minimumOrderValueRequired}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </BasicCard>

              {!rewardId && (
                <BasicCard>
                  <div>
                    <div>
                      <div className={headerStyle}>Discount Code</div>

                      <div className='mt-3'>
                        <InputCardItems>
                          <input
                            type='checkbox'
                            name='isDiscountCodePrefixRequired'
                            checked={formData.isDiscountCodePrefixRequired}
                            onChange={(e) => {
                              const value = e.target.checked;

                              setFormData((oldVal) => ({
                                ...oldVal,
                                isDiscountCodePrefixRequired: value,
                              }));
                            }}
                          />
                          <label>Add a prefix to discount code</label>
                        </InputCardItems>

                        <div>
                          <input
                            className={clsx(inputStyle, 'w-[350px]')}
                            value={formData.discountCodePrefix}
                            name='discountCodePrefix'
                            onChange={handleChange}
                            disabled={!formData.isDiscountCodePrefixRequired}
                          />
                        </div>
                      </div>
                    </div>

                    <hr className='my-4' />

                    <span className={clsx(noteStyle)}>
                      An example discount code will look like this:{' '}
                    </span>
                    <span className='text-[13px] text-semibold'>
                      {formData.discountCodePrefix}
                      {formData.discountCode}
                    </span>
                  </div>
                </BasicCard>
              )}
              <BasicCard>
                <div>
                  <div>
                    <div className={headerStyle}>Reward Expiry</div>

                    <div className='mt-3'>
                      <InputCardItems>
                        <input
                          type='checkbox'
                          name='isRewardExpiry'
                          checked={formData.isRewardExpiry}
                          onChange={(e) => {
                            const value = e.target.checked;

                            setFormData((oldVal) => ({
                              ...oldVal,
                              isRewardExpiry: value,
                            }));
                          }}
                        />
                        <label>
                          Expire issued reward after an elapsed time
                        </label>
                      </InputCardItems>

                      <div className='flex gap-3'>
                        <input
                          className={clsx(inputStyle, 'w-[200px]')}
                          type='date'
                          placeholder='Date'
                          disabled={!formData.isRewardExpiry}
                          name='expiryDate'
                          value={formData.expiryDate}
                          onChange={handleChange}
                          min={currentDate}
                        />
                        <input
                          className={clsx(inputStyle, 'w-[200px]')}
                          type='time'
                          placeholder='Time'
                          disabled={!formData.isRewardExpiry}
                          name='expiryTime'
                          value={formData.expiryTime}
                          onChange={handleChange}
                          min={currentTime}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </BasicCard>

              <BasicCard>
                <div className={clsx(headerStyle)}>Customer eligibility</div>

                <div>
                  <InputCardItems>
                    <input
                      type='checkbox'
                      name='isLimited'
                      checked={formData.isLimited}
                      onChange={(e) => {
                        const value = e.target.checked;

                        setFormData((oldVal) => ({
                          ...oldVal,
                          isLimited: value,
                        }));
                      }}
                    />
                    <label>Limit to customers based on VIP tiers</label>
                  </InputCardItems>

                  {formData.isLimited && (
                    <>
                      <div className='mb-3'>
                        <input
                          type='radio'
                          name='tierSelection'
                          value='included'
                          checked={formData.tierSelection === 'included'}
                          onChange={handleChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          <span className='text-bold'>Include</span> Specific
                          VIP tier
                        </label>
                        <Select
                          name='includedTierIds'
                          options={tireData}
                          defaultValue={formData.includedTierIds}
                          isMulti
                          isDisabled={formData.tierSelection !== 'included'}
                          onChange={(selectedOptions) =>
                            handleMultiSelectChange(
                              selectedOptions,
                              'includedTierIds'
                            )
                          }
                        />
                      </div>

                      <div className='mb-3'>
                        <input
                          type='radio'
                          name='tierSelection'
                          value='excluded'
                          checked={formData.tierSelection === 'excluded'}
                          onChange={handleChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          <span className='text-bold'>Exclude</span> Specific
                          VIP tier
                        </label>
                        <Select
                          name='excludedTierIds'
                          options={tireData}
                          value={formData.excludedTierIds}
                          defaultValue={formData.excludedTierIds}
                          isMulti
                          isDisabled={formData.tierSelection !== 'excluded'}
                          onChange={(selectedOptions) =>
                            handleMultiSelectChange(
                              selectedOptions,
                              'excludedTierIds'
                            )
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              </BasicCard>
            </div>

            <div className='w-[40%]'>
              <BasicCard>
                <div>
                  <div className={headerStyle}>Summary</div>
                  <div className={noteStyle}>
                    Customers earn 1 points for every ₹1 spent{' '}
                  </div>

                  <div className='w-full border my-5' />

                  <div>
                    <div className={headerStyle}>Status</div>

                    <div>
                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='isActive'
                          value='active'
                          onChange={handleChange}
                          checked={formData.isActive === 'active'}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Active
                        </label>
                      </div>

                      <div>
                        {' '}
                        <input
                          type='radio'
                          name='isActive'
                          value='disabled'
                          onChange={handleChange}
                          checked={formData.isActive === 'disabled'}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Disabled
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </BasicCard>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DiscountByType;
